import {
  Box,
  DefaultErrorMessage,
  dialogErrorImage,
  ErrorBoundary,
  Line,
  Text,
} from "@prismicio/editor-ui"
import { type PropsWithChildren, Suspense } from "react"

import { DocumentListBackButton } from "../app/components/DocumentListBackButton"
import { useContentOffset, useHeaderOffset } from "../app/layout/useOffset"
import { useRepositoryMetaTitle } from "../app/routing"
import {
  headerBottomMargin,
  headerHeight,
  mainContentMaxWidth,
  sidePanelWidth,
} from "../document/documentLayout"
import {
  DocumentListLayout,
  sidePanelPadding,
} from "../documents/documentListLayout"
import { SettingsPageMenu, SettingsPageMenuSkeleton } from "./SettingsPageMenu"

type SettingsPageProps = PropsWithChildren<{ pageName: string }>

export function SettingsPage(props: SettingsPageProps) {
  const { children, pageName } = props

  useRepositoryMetaTitle(pageName)

  return (
    <Suspense fallback={<SettingsPageSkeleton />}>
      <DocumentListLayout
        header={<SettingsPageHeader />}
        sidePanel={
          <SettingsPageSidePanel>
            <SettingsPageMenu />
          </SettingsPageSidePanel>
        }
        body={
          <SettingsPageBody>
            <ErrorBoundary renderError={renderSettingsPageBodyError}>
              {children}
            </ErrorBoundary>
          </SettingsPageBody>
        }
      />
    </Suspense>
  )
}

function SettingsPageHeader() {
  const { headerOffset } = useHeaderOffset()

  return (
    <Box
      display="flex"
      flexDirection="column"
      position="fixed"
      top={headerOffset}
      height={headerHeight}
      border={{ bottom: true }}
      backgroundColor="grey2"
      minWidth="100%"
      width="100vw"
    >
      <Line color="purple" />
      <Box justifyContent="center" flexGrow={1}>
        <Box width={mainContentMaxWidth} alignItems="center" gap={24}>
          <Box padding={{ bottom: 12, top: 12 }}>
            <Box alignItems="center" gap={16} minWidth={sidePanelWidth}>
              <DocumentListBackButton />
              <Text component="span" variant="bold">
                Settings
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

function SettingsPageSidePanel(props: PropsWithChildren) {
  const { children } = props
  const { contentOffset } = useContentOffset()

  return (
    <Box
      flexGrow={1}
      overflowY="auto"
      position="fixed"
      flexDirection="column"
      height={`calc(100vh - ${contentOffset}px)`}
      width={sidePanelWidth}
      padding={{ top: headerBottomMargin, ...sidePanelPadding }}
      border={{ right: true }}
      borderStyle="dashed"
    >
      {children}
    </Box>
  )
}

function SettingsPageBody(props: PropsWithChildren) {
  const { children } = props
  return (
    <Box
      flexDirection="column"
      gap={24}
      padding={{ left: 24, top: 24, bottom: 24 }}
      width="100%"
    >
      {children}
    </Box>
  )
}

function renderSettingsPageBodyError() {
  return (
    <Box height="100%" alignItems="center" justifyContent="center">
      <DefaultErrorMessage
        title="Request failed"
        description="An error occurred while fetching settings."
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}

function SettingsPageSkeleton() {
  return (
    <DocumentListLayout
      header={<SettingsPageHeader />}
      sidePanel={
        <SettingsPageSidePanel>
          <SettingsPageMenuSkeleton />
        </SettingsPageSidePanel>
      }
      body={<SettingsPageBody />}
    />
  )
}
